import {
  IReleaseNote,
  IReleaseNoteContent,
  IReleaseNotePublishBlocker,
  ReleaseNoteAudience,
  ReleaseNoteLanguage,
  ReleaseNoteMerchantRole,
  ReleaseNotePublishStatus,
} from "../../interfaces/releaseNote.interface";

export const AUDIENCE_ORDER: ReleaseNoteAudience[] = [
  ReleaseNoteAudience.Internal,
  ReleaseNoteAudience.Partners,
  ReleaseNoteAudience.Merchants,
];

export const AUDIENCE_LABELS: Record<ReleaseNoteAudience, string> = {
  [ReleaseNoteAudience.Internal]: "Internal",
  [ReleaseNoteAudience.Partners]: "Partners",
  [ReleaseNoteAudience.Merchants]: "Merchants",
};

export const LANGUAGE_ORDER: ReleaseNoteLanguage[] = [
  ReleaseNoteLanguage.English,
  ReleaseNoteLanguage.Dutch,
  ReleaseNoteLanguage.Italian,
];

export const LANGUAGE_LABELS: Record<ReleaseNoteLanguage, string> = {
  [ReleaseNoteLanguage.English]: "EN",
  [ReleaseNoteLanguage.Dutch]: "NL",
  [ReleaseNoteLanguage.Italian]: "IT",
};

export const MERCHANT_ROLE_ORDER: ReleaseNoteMerchantRole[] = [
  ReleaseNoteMerchantRole.Employee,
  ReleaseNoteMerchantRole.Admin,
  ReleaseNoteMerchantRole.Owner,
];

export const MERCHANT_ROLE_LABELS: Record<ReleaseNoteMerchantRole, string> = {
  [ReleaseNoteMerchantRole.Employee]: "Employees",
  [ReleaseNoteMerchantRole.Admin]: "Admins",
  [ReleaseNoteMerchantRole.Owner]: "Owners",
};

export function audienceHasContent(
  note: IReleaseNote,
  audience: ReleaseNoteAudience
): boolean {
  return note.content.some(
    (c) =>
      c.audience === audience && (c.title.trim() !== "" || c.body.trim() !== "")
  );
}

export function audienceLanguages(
  note: IReleaseNote,
  audience: ReleaseNoteAudience
): ReleaseNoteLanguage[] {
  return LANGUAGE_ORDER.filter((language) =>
    note.content.some(
      (c) =>
        c.audience === audience &&
        c.language === language &&
        (c.title.trim() !== "" || c.body.trim() !== "")
    )
  );
}

export function audienceStatus(
  note: IReleaseNote,
  audience: ReleaseNoteAudience
): ReleaseNotePublishStatus {
  return (
    note.audienceStates.find((s) => s.audience === audience)?.status ??
    ReleaseNotePublishStatus.Draft
  );
}

export function overallStatus(note: IReleaseNote): ReleaseNotePublishStatus {
  return note.audienceStates.some(
    (s) => s.status === ReleaseNotePublishStatus.Published
  )
    ? ReleaseNotePublishStatus.Published
    : ReleaseNotePublishStatus.Draft;
}

export function releaseNoteDisplayTitle(note: IReleaseNote): string {
  const english = note.content.find(
    (c) => c.language === ReleaseNoteLanguage.English && c.title.trim() !== ""
  );
  if (english) return english.title.trim();
  const anyTitled = note.content.find((c) => c.title.trim() !== "");
  return anyTitled ? anyTitled.title.trim() : "";
}

export function findPublishBlockers(
  content: IReleaseNoteContent[]
): IReleaseNotePublishBlocker[] {
  return content.flatMap((c) => {
    const missing: ("title" | "body")[] = [];
    if (c.title.trim() === "") missing.push("title");
    if (c.body.trim() === "") missing.push("body");
    return missing.length > 0
      ? [{ audience: c.audience, language: c.language, missing }]
      : [];
  });
}

export function filterReleaseNotesByStatus(
  notes: IReleaseNote[],
  statusFilter: ReleaseNotePublishStatus | "all"
): IReleaseNote[] {
  if (statusFilter === "all") return notes;
  return notes.filter((note) => overallStatus(note) === statusFilter);
}
